﻿<template>
  <div class="webrtc-player relative w-full h-full bg-black group overflow-hidden">
    <img
      v-if="posterSrc && !isPlaying"
      :src="posterSrc"
      alt=""
      aria-hidden="true"
      class="absolute inset-0 w-full h-full object-contain pointer-events-none select-none" />

    <!-- Video element -->
    <video
      ref="videoElement"
      :class="['w-full h-full object-contain transition-opacity duration-200', isPlaying ? 'opacity-100' : 'opacity-0']"
      autoplay="true"
      :muted="muted"
      :controls="controls"
      @playing="onPlaying"
      @error="onVideoError" />

    <!-- Loading / Status Overlay -->
    <div
      v-if="showStatus && (isLoading || error || (!connected && !connecting))"
      class="media-failure-overlay absolute inset-0 flex items-center justify-center bg-black/60 z-10 transition-opacity duration-300 pointer-events-none">
      <div class="flex flex-col items-center gap-3 text-white pointer-events-auto max-h-full min-h-0">
        <!-- Connecting -->
        <template v-if="isLoading">
          <div class="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
        </template>

        <!-- Error -->
        <template v-else-if="error">
          <div
            class="media-failure-box bg-red-500/10 p-4 rounded-xl backdrop-blur-sm border border-red-500/20 flex flex-col items-center max-h-full min-h-0 overflow-y-auto">
            <span class="media-failure-box__icon material-symbols-outlined text-2xl text-red-500 mb-2">warning</span>
            <p class="media-failure-box__sentence text-xs text-red-400 font-medium px-2 text-center mb-3">
              {{ error }}
            </p>
            <!-- The cause, under the sentence (card t_4d5b3263): a spent budget,
                 an ICE that gave up and an offer that gathered nothing are three
                 different next actions. Only the reason is said — the gathering
                 census and the timings stay in the console and the hub's log. -->
            <p
              v-if="mediaPathCause"
              data-testid="media-path-cause"
              class="media-failure-box__cause text-[10px] leading-snug text-red-300/80 px-2 text-center mb-3">
              {{ mediaPathCause }}
            </p>
            <button
              v-if="showRetry"
              @click="reconnect"
              class="px-4 py-1.5 bg-red-500 hover:bg-red-400 text-white rounded-lg text-xs font-medium transition-colors">
              {{ tt('Retry', 'Poskusi znova') }}
            </button>
          </div>
        </template>

        <!-- Disconnected -->
        <template v-else-if="!connected && !connecting">
          <div class="flex flex-col items-center opacity-50">
            <span class="material-symbols-outlined text-3xl mb-2">videocam_off</span>
            <button
              v-if="showRetry"
              @click="connect"
              class="px-3 py-1 bg-white/10 hover:bg-white/20 rounded text-xs transition-colors">
              {{ tt('Connect', 'Poveži') }}
            </button>
          </div>
        </template>
      </div>
    </div>

    <!-- Live Badge -->
    <div
      v-if="connected && isPlaying"
      class="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 bg-black/40 backdrop-blur-md rounded-md border border-white/10 shadow-lg pointer-events-none transition-all duration-500">
      <span class="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)] animate-pulse"></span>
      <span class="text-[10px] font-bold tracking-widest text-white/90">{{ tt('LIVE', 'V ŽIVO') }}</span>
    </div>

    <!-- Intercom Call button (SIP doorphones) -->
    <button
      v-if="showCall && sipDevice"
      @click.stop="callDoorphone"
      :disabled="callDisabled"
      :title="callTitle"
      class="absolute bottom-3 right-3 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-md border transition-colors"
      :class="
        callDisabled
          ? 'bg-gray-700/60 border-gray-600/40 text-gray-400 cursor-not-allowed'
          : 'bg-blue-600/80 hover:bg-blue-500 border-blue-400/40 text-white shadow-lg shadow-blue-900/30'
      ">
      <span class="material-symbols-outlined text-sm">call</span>
      <span class="text-[10px] font-bold tracking-widest">{{ tt('CALL', 'POKLIČI') }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, computed, shallowRef } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  invalidateCameraStreamEndpoints,
  resolveCameraStreamSession,
  type CameraStreamPreference,
  type CameraStreamSession,
} from '../composables/useCameraStreamSession';
import { useIntercomStore } from '../stores/intercom';
import { useToast } from '../composables/useToast';
import { mediaPathReasonKey } from '../services/mediaPath';
import { tt } from '../utils/consoleText';

const props = withDefaults(
  defineProps<{
    cameraId: number | string;
    posterSrc?: string | null;
    autoConnect?: boolean;
    muted?: boolean;
    controls?: boolean;
    showStatus?: boolean;
    showRetry?: boolean;
    streamPreference?: CameraStreamPreference;
    /** Show an intercom Call button when the camera has SIP configured */
    showCall?: boolean;
  }>(),
  {
    posterSrc: null,
    autoConnect: true,
    muted: true,
    controls: false,
    showStatus: true,
    showRetry: true,
    streamPreference: 'main',
    showCall: false,
  },
);

const emit = defineEmits<{
  connected: [];
  disconnected: [];
  error: [error: string];
  playing: [];
  audioAvailable: [hasAudio: boolean];
}>();

// Video state
const videoElement = ref<HTMLVideoElement>();
const isPlaying = ref(false);
const session = shallowRef<CameraStreamSession | null>(null);
const resolutionError = ref<string | null>(null);
let sessionRequestId = 0;

// Intercom call support — detect SIP-enabled doorphones by camera id
const intercomStore = useIntercomStore();
const toast = useToast();
const { t } = useI18n();

const sipDevice = computed(() => intercomStore.sipDevices.find((d) => d.id === String(props.cameraId)));
const callDisabled = computed(() => !!intercomStore.activeCall || !sipDevice.value?.liveStatus?.online);
const callTitle = computed(() => {
  if (intercomStore.activeCall) return tt('A call is already active', 'Klic je že aktiven');
  if (!sipDevice.value?.liveStatus?.online)
    return tt(
      'Doorphone not registered yet — enable SIP on this camera in the Intercom settings',
      'Domofon še ni registriran — omogočite SIP za to kamero v nastavitvah domofona',
    );
  return tt('Call doorphone', 'Pokliči vhodni telefon');
});

async function callDoorphone() {
  if (callDisabled.value) return;
  const call = await intercomStore.callDevice(String(props.cameraId));
  if (!call) {
    toast.error(intercomStore.error || tt('Failed to call doorphone', 'Klic domofona ni uspel'));
  }
}

// Directly set muted DOM property when prop changes (Vue :muted binding
// can use setAttribute which doesn't toggle audio after initial render)
watch(
  () => props.muted,
  (val) => {
    if (videoElement.value) {
      videoElement.value.muted = val;
    }
  },
);

const connecting = computed(() => session.value?.connecting.value ?? false);
const connected = computed(() => session.value?.connected.value ?? false);

/**
 * The media path never came up (card `t_20b35b28`): a WHEP answer the camera
 * accepted, and an ICE that never completed — the measured symptom that used to
 * leave this surface spinning in `connecting` forever with no sentence at all.
 * The state is the session's own (`services/mediaPath.ts` bounds the wait);
 * the sentence is this surface's, in every catalogue the app shell renders.
 */
const mediaPathFailed = computed(() => !connected.value && session.value?.mediaPath.value === 'no-media-path');

const error = computed(
  () =>
    resolutionError.value ||
    (mediaPathFailed.value ? t('views.cameras.mediaPathFailed') : null) ||
    session.value?.error.value ||
    null,
);

/**
 * The cause, under the sentence (card `t_4d5b3263`): the session's own reason
 * names *which* failure this is — a spent budget (usually the hub's network),
 * an ICE that gave up, or an offer that gathered nothing at all. Read through
 * `t()` so it follows a locale switch without a reconnect, and `null` for every
 * failure that is not about a media path (those carry their own sentence, and
 * the reason would misdescribe them).
 */
const mediaPathCause = computed(() => {
  if (!mediaPathFailed.value) return null;
  const key = mediaPathReasonKey(session.value?.mediaPathDetail.value?.reason);
  return key ? t(key) : null;
});
const hasAudio = computed(() => session.value?.hasAudio.value ?? false);

const isLoading = computed(() => connecting.value || (connected.value && !isPlaying.value));

// Lifecycle
onMounted(() => {
  if (props.autoConnect) {
    connect();
  }

  // Ensure SIP device info is loaded so the call button knows the doorphone
  if (props.showCall && intercomStore.sipDevices.length === 0) {
    intercomStore.loadSIPDevices().catch(() => undefined);
  }
});

onUnmounted(() => {
  detachCurrentSession();
});

watch(videoElement, (next, prev) => {
  if (prev && session.value) {
    session.value.detachVideoElement(prev);
  }

  if (next && session.value) {
    next.muted = props.muted;
    session.value.attachVideoElement(next);
  }
});

watch(connected, (next, prev) => {
  if (next && !prev) {
    emit('connected');
  }

  if (!next && prev) {
    emit('disconnected');
  }
});

watch(error, (next, prev) => {
  if (next && next !== prev) {
    emit('error', next);
  }
});

watch(
  hasAudio,
  (next, prev) => {
    if (next !== prev) {
      emit('audioAvailable', next);
    }
  },
  { immediate: true },
);

watch(
  () => [props.cameraId, props.streamPreference, props.autoConnect] as const,
  async () => {
    isPlaying.value = false;

    if (!props.cameraId) {
      detachCurrentSession();
      return;
    }

    if (props.autoConnect) {
      await connect();
      return;
    }

    detachCurrentSession();
  },
);

function detachCurrentSession() {
  sessionRequestId += 1;

  if (videoElement.value && session.value) {
    session.value.detachVideoElement(videoElement.value);
  }

  session.value = null;
  resolutionError.value = null;
  isPlaying.value = false;
}

function bindSession(nextSession: CameraStreamSession) {
  if (session.value === nextSession) {
    if (videoElement.value) {
      videoElement.value.muted = props.muted;
      nextSession.attachVideoElement(videoElement.value);
    }
    return;
  }

  if (videoElement.value && session.value) {
    session.value.detachVideoElement(videoElement.value);
  }

  session.value = nextSession;
  resolutionError.value = null;
  isPlaying.value = false;

  if (videoElement.value) {
    videoElement.value.muted = props.muted;
    nextSession.attachVideoElement(videoElement.value);
  }

  emit('audioAvailable', nextSession.hasAudio.value);
  if (nextSession.error.value) {
    emit('error', nextSession.error.value);
  }
  if (nextSession.connected.value) {
    emit('connected');
  }
}

async function resolveSession(): Promise<CameraStreamSession | null> {
  const cameraId = String(props.cameraId || '');
  if (!cameraId) {
    detachCurrentSession();
    return null;
  }

  const requestId = ++sessionRequestId;

  try {
    const nextSession = await resolveCameraStreamSession(cameraId, props.streamPreference);
    if (requestId !== sessionRequestId) {
      return null;
    }

    bindSession(nextSession);
    return nextSession;
  } catch (__caught: unknown) {
    const err = __caught as Error;
    if (requestId !== sessionRequestId) {
      return null;
    }

    console.error('[WebRTCPlayer] Failed to resolve session:', err);
    detachCurrentSession();
    resolutionError.value = err?.message || tt('Connection failed', 'Povezava ni uspela');
    return null;
  }
}

// Connect to camera via shared go2rtc session
async function connect(): Promise<boolean> {
  const nextSession = await resolveSession();
  if (!nextSession) {
    return false;
  }

  return nextSession.connect();
}

// Disconnect
function disconnect(): void {
  session.value?.disconnect(true);
}

// Reconnect
async function reconnect() {
  invalidateCameraStreamEndpoints(String(props.cameraId || ''));
  const nextSession = await resolveSession();
  if (!nextSession) {
    return;
  }

  await nextSession.reconnect();
}

// Event handlers
function onPlaying() {
  isPlaying.value = true;
  emit('playing');
}

function onVideoError(e: Event) {
  console.error('[WebRTCPlayer] Video error:', e);
  resolutionError.value = tt('Playback Error', 'Napaka predvajanja');
  isPlaying.value = false;
}

defineExpose({ connect, disconnect, reconnect });
</script>

<style scoped>
/* No extra styles needed */
</style>
